import { first_non_empty_number } from "./configure_signers_helpers";

function is_object_like(value) {
    return Boolean(value) && typeof value === "object";
}

function has_own_key(source, key) {
    return Reflect.has(source, key);
}

function read_property(source, key) {
    return Reflect.get(source, key);
}

function is_valid_array_index(items, index) {
    return Number.isInteger(index) && index >= 0 && index < items.length;
}

export function read_object_array(source, key) {
    if (!is_object_like(source)) {
        return [];
    }

    if (!has_own_key(source, key)) {
        return [];
    }

    const value = read_property(source, key);
    const safe_value = Array.isArray(value) ? value : [];
    return safe_value;
}

export function read_array_item(items, index) {
    if (!Array.isArray(items)) {
        return null;
    }

    if (!is_valid_array_index(items, index)) {
        return null;
    }

    const item = Reflect.get(items, index);
    return item;
}

export function get_slot_step_id(slot) {
    return slot?.step_id;
}

export function has_selected_user_id(slot) {
    return Boolean(String(slot?.selected_user_id || "").trim());
}

export function is_internal_slot(slot) {
    return slot?.source_type === "internal";
}

function get_matched_user_id(assigned_signer) {
    return Number(assigned_signer?.user_id || 0);
}

export function get_matched_user(user_options, assigned_signer) {
    const matched_user = user_options.find(function find_matching_user(user) {
        return user.user_id === get_matched_user_id(assigned_signer);
    }) || null;
    return matched_user;
}

export function get_external_name(source_type, assigned_signer) {
    if (source_type !== "external") {
        return "";
    }

    const external_name = String(assigned_signer?.full_name || "").trim() || "";
    return external_name;
}

function get_slot_selected_user_id(matched_user) {
    return matched_user ? String(matched_user.user_id) : "";
}

function get_slot_email(assigned_signer, matched_user) {
    return assigned_signer?.email || matched_user?.email || "";
}

function get_slot_assigned_signer_id(assigned_signer) {
    return assigned_signer?.id || null;
}

export function build_slot(
    step,
    safe_role_name,
    position,
    matched_user,
    assigned_signer,
    user_options,
    source_type,
    external_name,
    global_order
) {
    const slot = {
        key: `${step.id}-${position}`,
        global_order,
        step_id: step.id,
        step_order: step.order,
        slot_position: position,
        role_name: safe_role_name,
        entity_type_id: step.entity_type_id,
        source_type,
        selected_user_id: get_slot_selected_user_id(matched_user),
        external_name,
        email: get_slot_email(assigned_signer, matched_user),
        user_options,
        assigned_signer_id: get_slot_assigned_signer_id(assigned_signer)
    };
    return slot;
}

export function get_slot_source_type(matched_user, has_external_assignment, position, internal_capacity) {
    if (matched_user) {
        return "internal";
    }

    return has_external_assignment || position > internal_capacity ? "external" : "internal";
}

function should_keep_external_source(slot) {
    return slot.source_type === "external";
}

function has_slot_selection(slot) {
    return String(slot.selected_user_id || "").trim();
}

function resolve_slot_source_type(index, internal_capacity) {
    return index < internal_capacity ? "internal" : "external";
}

export function sync_single_slot_source_type(slot, index, internal_capacity) {
    if (should_keep_external_source(slot)) {
        return;
    }

    if (has_slot_selection(slot)) {
        return;
    }

    slot.source_type = resolve_slot_source_type(index, internal_capacity);
    if (slot.source_type === "external") {
        slot.email = "";
    }
}

export function get_assigned_signer_at_position(assigned_signers, position) {
    const assigned_signer = read_array_item(assigned_signers, position - 1);
    return assigned_signer;
}

export function get_step_assigned_signers(assigned_signers_by_step, step_id) {
    return read_object_array(assigned_signers_by_step, step_id);
}

export function get_step_user_options(user_options_by_entity_type, entity_type_id) {
    return read_object_array(user_options_by_entity_type, entity_type_id);
}

export function get_unique_step_ids(signer_slots) {
    const safe_signer_slots = Array.isArray(signer_slots) ? signer_slots : [];
    const step_ids = [...new Set(safe_signer_slots.map(function map_step_id(slot) {
        return get_slot_step_id(slot);
    }).filter(Boolean))];
    return step_ids;
}

export function get_sorted_step_slots(step_slots) {
    const sorted_slots = [...step_slots].sort(function sort_step_slots(first_slot, second_slot) {
        return (first_slot.slot_position || 0) - (second_slot.slot_position || 0);
    });
    return sorted_slots;
}

export function get_step_internal_capacity(step_slots) {
    if (!step_slots.length) {
        return 0;
    }

    const available_options = Array.isArray(step_slots[0]?.user_options) ? step_slots[0].user_options.length : 0;
    return Math.min(step_slots.length, available_options);
}

export function get_slot_swap_index(index, direction) {
    return index + direction;
}

function is_swap_index_out_of_bounds(signer_slots, swap_index) {
    return swap_index < 0 || swap_index >= signer_slots.length;
}

function is_same_step_slot(current_slot, target_slot) {
    return Boolean(current_slot && target_slot && current_slot.step_id === target_slot.step_id);
}

export function can_swap_slots(signer_slots, index, swap_index) {
    if (is_swap_index_out_of_bounds(signer_slots, swap_index)) {
        return false;
    }

    const current_slot = read_array_item(signer_slots, index);
    const target_slot = read_array_item(signer_slots, swap_index);
    const can_swap = is_same_step_slot(current_slot, target_slot);
    return can_swap;
}

export function swap_slots(signer_slots, first_index, second_index) {
    const first_slot = read_array_item(signer_slots, first_index);
    const second_slot = read_array_item(signer_slots, second_index);
    if (!first_slot || !second_slot) {
        return signer_slots;
    }

    const updated_slots = [...signer_slots];
    updated_slots.splice(first_index, 1, second_slot);
    updated_slots.splice(second_index, 1, first_slot);
    return updated_slots;
}

function get_slot_count(step, fallback_signer_count) {
    return step.expected_signer_count || fallback_signer_count;
}

function get_safe_role_name(step, step_index) {
    return step.entity_type_name || `Signer role ${step_index + 1}`;
}

function get_internal_capacity(slot_count, user_options) {
    return Math.min(slot_count, user_options.length);
}

function has_external_assignment(matched_user, assigned_signer) {
    return Boolean(!matched_user && assigned_signer?.email);
}

function create_slot_for_position(
    step,
    safe_role_name,
    position,
    assigned_signers,
    user_options,
    internal_capacity,
    global_order
) {
    const assigned_signer = get_assigned_signer_at_position(assigned_signers, position);
    const matched_user = get_matched_user(user_options, assigned_signer);
    const external_assignment = has_external_assignment(matched_user, assigned_signer);
    const source_type = get_slot_source_type(matched_user, external_assignment, position, internal_capacity);
    const external_name = get_external_name(source_type, assigned_signer);
    const slot = build_slot(
        step,
        safe_role_name,
        position,
        matched_user,
        assigned_signer,
        user_options,
        source_type,
        external_name,
        global_order
    );
    return slot;
}

export function build_signer_slots(workflow_steps, signer_config, assigned_signers_by_step, user_options_by_entity_type) {
    const fallback_signer_count = first_non_empty_number(signer_config?.expected_signer_count) || 1;
    const slots = [];
    let global_index = 0;

    workflow_steps.forEach(function build_step_slots(step, step_index) {
        const assigned_signers = get_step_assigned_signers(assigned_signers_by_step, step.id);
        const slot_count = get_slot_count(step, fallback_signer_count);
        const user_options = get_step_user_options(user_options_by_entity_type, step.entity_type_id);
        const safe_role_name = get_safe_role_name(step, step_index);
        const internal_capacity = get_internal_capacity(slot_count, user_options);

        for (let position = 1; position <= slot_count; position += 1) {
            global_index += 1;
            const slot = create_slot_for_position(
                step,
                safe_role_name,
                position,
                assigned_signers,
                user_options,
                internal_capacity,
                global_index
            );
            slots.push(slot);
        }
    });

    return slots;
}
